import { useEffect, useMemo } from 'react';
import { LocalizedLink as Link } from '../components/LocalizedLink';
import { useTranslation, Trans } from 'react-i18next';
import { BookOpen } from 'lucide-react';
import { SEO } from '../components/SEO';
import { useLocalizedSeo } from '../hooks/useLocalizedSeo';
import { PublicHeader } from '../components/PublicHeader';
import { PublicFooter } from '../components/PublicFooter';

const SITE_URL = 'https://bolaoelite.com.br';

type TermCategory = 'Bolão' | 'Futebol';

interface Term {
  id: string;
  term: string;
  category: TermCategory;
  definition: string;
}

// Termos organizados em 2 categorias: Bolão e Futebol. `term`/`definition`
// são os defaults PT-BR; os textos exibidos vêm da tabela `translations`
// via t(`glossary.t.${id}.name`/.def).
const TERMS: Term[] = [
  // ─── BOLÃO ──────────────────────────────────────────────────────────────────
  { id: 'bolao', category: 'Bolão', term: 'Bolão', definition: 'Competição entre amigos onde cada participante tenta acertar resultados de partidas de um campeonato. No Bolão Elite, é a unidade básica da plataforma — você cria ou entra em bolões pra competir.' },
  { id: 'bolao-privado', category: 'Bolão', term: 'Bolão privado', definition: 'Bolão fechado, em que só entram participantes convidados pelo criador via link. Ideal para grupo de amigos, família ou colegas de trabalho.' },
  { id: 'bolao-publico', category: 'Bolão', term: 'Bolão público', definition: 'Bolão aberto a qualquer usuário da plataforma, sem precisar de convite. Os bolões públicos são criados pela administração do Bolão Elite — qualquer pessoa (gratuita ou Premium) pode participar livremente.' },
  { id: 'palpite', category: 'Bolão', term: 'Palpite', definition: 'O placar que você acredita que vai acontecer em uma partida específica. No Bolão Elite, palpita-se sempre o placar exato (ex: 2x1). A pontuação é calculada comparando seu palpite com o resultado oficial.' },
  { id: 'placar-exato', category: 'Bolão', term: 'Placar exato', definition: 'Acertar o número exato de gols de cada time (ex: palpitou 2x1 e foi 2x1). Costuma dar a maior pontuação do sistema.' },
  { id: 'regra-a', category: 'Bolão', term: 'Regra A (Simples)', definition: 'Regra de pontuação onde só o placar exato vale ponto: acertou o placar = 1 ponto, qualquer outra coisa = 0 pontos. Ideal para grupos que querem desafio puro.' },
  { id: 'regra-b', category: 'Bolão', term: 'Regra B (Intermediária)', definition: 'Regra de pontuação mais popular: placar exato = 3 pontos, acertar só o vencedor ou empate = 1 ponto, errar = 0 pontos.' },
  { id: 'regra-c', category: 'Bolão', term: 'Regra C (Avançada)', definition: 'Regra granular: placar exato = 10 pontos, vencedor ou empate correto = 5 pontos, número de gols de um dos times correto = 2 pontos, errar tudo = 0 pontos.' },
  { id: 'tempo-bloqueio', category: 'Bolão', term: 'Tempo de bloqueio', definition: 'Tempo antes do início da partida em que os palpites ficam bloqueados (não dá mais pra criar nem alterar). O criador do bolão escolhe entre 10, 30 ou 60 minutos.' },
  { id: 'palpites-ocultos', category: 'Bolão', term: 'Palpites ocultos', definition: 'Configuração em que os palpites ficam ocultos para os outros participantes até o início de cada partida. O criador do bolão ativa essa opção — disponível em qualquer bolão, sem custo.' },
  { id: 'rodada', category: 'Bolão', term: 'Rodada', definition: 'Conjunto de partidas de um campeonato que acontecem em uma semana (ou intervalo definido pelo torneio). A pontuação é geralmente exibida tanto por rodada quanto acumulada.' },
  { id: 'pontuacao-acumulada', category: 'Bolão', term: 'Pontuação acumulada', definition: 'Total de pontos do participante somando todas as rodadas desde o início do campeonato.' },
  { id: 'ranking-bolao', category: 'Bolão', term: 'Ranking do bolão', definition: 'Classificação dos participantes de um bolão específico, ordenada pela pontuação acumulada (do maior pro menor).' },
  { id: 'ranking-global', category: 'Bolão', term: 'Ranking global', definition: 'Classificação entre todos os usuários da plataforma em uma mesma competição. Ex: melhor palpiteiro do Brasileirão 2026 considerando todos os usuários do Bolão Elite.' },
  { id: 'concurso-cultural', category: 'Bolão', term: 'Concurso cultural', definition: 'Modalidade prevista na Lei 5.768/71 e Decreto 70.951/72, em que pode haver premiação simbólica (ex: Pix para top 3) sem caracterizar jogo a dinheiro, desde que a participação seja gratuita e o resultado dependa de habilidade. Bolões públicos no Bolão Elite podem operar nessa modalidade.' },
  { id: 'premiacao', category: 'Bolão', term: 'Premiação', definition: 'Recompensa eventual dada aos melhores colocados de um bolão. No Bolão Elite, quando existe, segue a modalidade de concurso cultural — sem cobrança pra participar.' },
  { id: 'administrador', category: 'Bolão', term: 'Administrador do bolão', definition: 'Usuário que criou o bolão. Pode editar regras, remover participantes problemáticos e encerrar o bolão.' },
  { id: 'convite', category: 'Bolão', term: 'Convite', definition: 'Link único gerado pelo administrador de um bolão privado. Quem recebe pode entrar com um clique (mesmo sem ter conta — cria na hora).' },

  // ─── FUTEBOL ────────────────────────────────────────────────────────────────
  { id: 'mata-mata', category: 'Futebol', term: 'Mata-mata', definition: 'Fase de um campeonato em que o time perdedor é eliminado imediatamente. Geralmente vem após a fase de grupos e pode ter jogos de ida/volta ou jogo único.' },
  { id: 'fase-grupos', category: 'Futebol', term: 'Fase de grupos', definition: 'Fase em que os times são divididos em grupos pequenos (3 a 6 times). Cada um joga contra todos do grupo, e os melhores avançam para o mata-mata.' },
  { id: 'pontos-corridos', category: 'Futebol', term: 'Pontos corridos', definition: 'Sistema em que todos os times jogam contra todos, ida e volta (ou só uma vez). O campeão é quem somar mais pontos no fim. É o formato do Brasileirão Série A.' },
  { id: 'agregado', category: 'Futebol', term: 'Agregado', definition: 'Soma dos placares de duas partidas (jogo de ida e jogo de volta) entre os mesmos times. Quem tiver maior agregado avança.' },
  { id: 'gol-fora', category: 'Futebol', term: 'Gol fora de casa', definition: 'Critério de desempate em confrontos de ida e volta — em caso de empate no agregado, ganha quem fez mais gols como visitante. Várias competições removeram essa regra recentemente.' },
  { id: 'penaltis', category: 'Futebol', term: 'Pênaltis', definition: 'Disputa final em jogos eliminatórios que terminam empatados. Cada time bate 5 pênaltis alternados; se persistir empate, vai pra morte súbita (um pra um).' },
  { id: 'prorrogacao', category: 'Futebol', term: 'Prorrogação', definition: 'Tempo extra de 30 minutos (dois tempos de 15) jogado após empate em jogos eliminatórios. Se permanecer empatado, vai pra disputa de pênaltis.' },
  { id: 'saldo-gols', category: 'Futebol', term: 'Saldo de gols', definition: 'Diferença entre gols marcados e sofridos. Usado como critério de desempate em classificações por pontos corridos.' },
  { id: 'criterio-desempate', category: 'Futebol', term: 'Critério de desempate', definition: 'Regra usada quando dois times empatam em pontos. Ordem típica: número de vitórias, saldo de gols, gols feitos, confronto direto, cartões.' },
  { id: 'cabeca-chave', category: 'Futebol', term: 'Cabeça-de-chave', definition: 'Time melhor ranqueado em um sorteio de fase de grupos ou mata-mata, garantindo que não enfrente outros times de mesma força no início.' },
  { id: 'wo', category: 'Futebol', term: 'WO (WxO)', definition: 'Vitória por ausência do adversário (Walk Over). Acontece quando um dos times não comparece à partida. Placar oficial costuma ser 3x0 ou 1x0 a critério do regulamento.' },
  { id: 'hat-trick', category: 'Futebol', term: 'Hat-trick', definition: 'Quando um mesmo jogador marca 3 gols em uma única partida.' },
  { id: 'poquer', category: 'Futebol', term: 'Pôquer', definition: 'Quatro gols do mesmo jogador em uma partida. Mais raro que um hat-trick.' },
  { id: 'gol-contra', category: 'Futebol', term: 'Gol contra', definition: 'Quando um jogador marca contra o próprio gol. O gol vai pro time adversário, mas o autor é o próprio defensor.' },
  { id: 'var', category: 'Futebol', term: 'VAR', definition: 'Video Assistant Referee — sistema de arbitragem por vídeo introduzido na FIFA em 2018. Confere gols, pênaltis, expulsões e identificações erradas.' },
  { id: 'decreto-classificacao', category: 'Futebol', term: 'Decreto de classificação', definition: 'Em fases de grupos com avanço dos "melhores terceiros colocados" (como na Copa do Mundo 2026), critério oficial que determina quais 3os de grupos diferentes avançam para o mata-mata.' },
];

const CATEGORIES: TermCategory[] = ['Bolão', 'Futebol'];

function catAnchor(cat: TermCategory) {
  return `categoria-${cat.toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '')}`;
}

export default function Glossario() {
  const { t, i18n } = useTranslation('institutional');
  const lang = i18n.language;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const termName = (term: Term) => t(`glossary.t.${term.id}.name`, term.term);
  const termDef = (term: Term) => t(`glossary.t.${term.id}.def`, term.definition);
  const catLabel = (cat: TermCategory) =>
    cat === 'Bolão'
      ? t('glossary.cat_bolao', 'Bolão')
      : t('glossary.cat_futebol', 'Futebol');
  const sectionLabel = (cat: TermCategory) =>
    cat === 'Bolão'
      ? t('glossary.section_bolao', 'Termos de Bolão')
      : t('glossary.section_futebol', 'Termos de Futebol');

  // JSON-LD DefinedTermSet — schema oficial pra glossários, melhora indexação
  // em "Featured Snippets" e respostas de IA.
  const jsonLd = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'DefinedTermSet',
      name: t('glossary.seo_title', 'Glossário do Bolão Elite — Termos de bolão e futebol'),
      description: t('glossary.seo_desc', 'Dicionário de termos usados em bolões e no futebol.'),
      url: `${SITE_URL}/glossario`,
      inLanguage: lang,
      hasDefinedTerm: TERMS.map(term => ({
        '@type': 'DefinedTerm',
        name: termName(term),
        description: termDef(term),
        inDefinedTermSet: `${SITE_URL}/glossario`,
        termCode: catLabel(term.category),
      })),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, lang]
  );

  const grouped = useMemo(() => {
    return CATEGORIES.reduce<Record<TermCategory, Term[]>>(
      (acc, cat) => {
        acc[cat] = TERMS.filter(term => term.category === cat).sort((a, b) =>
          termName(a).localeCompare(termName(b), lang)
        );
        return acc;
      },
      { Bolão: [], Futebol: [] }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, lang]);

  const seo = useLocalizedSeo('glossario');

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <SEO
        title={t('glossary.seo_title', 'Glossário do Bolão Elite — Termos de bolão e futebol')}
        description={t('glossary.seo_desc', 'Dicionário com dezenas de termos sobre bolões e futebol: palpites ocultos, mata-mata, concurso cultural, ranking global, agregado e muito mais.')}
        canonical={seo.canonical}
        ogUrl={seo.ogUrl}
        locale={seo.locale}
        alternates={seo.alternates}
        keywords="glossário bolão, dicionário futebol, termos palpite, mata-mata, agregado, ranking global"
        jsonLd={jsonLd}
      />

      <PublicHeader />

      <section className="bg-gradient-to-br from-primary-700 via-primary-800 to-primary-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <nav className="text-sm text-white/70 mb-3" aria-label="breadcrumb">
            <Link to="/" className="hover:text-white transition-colors">{t('common:nav.home', 'Início')}</Link>
            <span className="mx-2">/</span>
            <span className="text-white">{t('glossary.breadcrumb', 'Glossário')}</span>
          </nav>
          <div className="flex items-center gap-3 mb-2">
            <BookOpen className="w-7 h-7 text-primary-200" />
            <span className="text-xs font-semibold text-primary-200 uppercase tracking-widest">
              {t('glossary.eyebrow', 'Dicionário')}
            </span>
          </div>
          <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight leading-tight">
            {t('glossary.h1', 'Glossário do Bolão Elite')}
          </h1>
          <p className="mt-5 text-base sm:text-lg text-white/80 leading-relaxed max-w-3xl">
            <Trans i18nKey="glossary.intro" t={t} values={{ count: TERMS.length }}>
              {'{{count}}'} termos explicados em linguagem clara, divididos em duas categorias: <strong>bolão</strong> e <strong>futebol</strong>. Use este dicionário sempre que bater uma dúvida sobre uma palavra técnica.
            </Trans>
          </p>
        </div>
      </section>

      <main className="flex-1 max-w-4xl mx-auto w-full px-4 sm:px-6 py-10 sm:py-14 space-y-12">
        {/* Sumário das categorias */}
        <nav className="bg-white border border-gray-200 rounded-2xl p-5" aria-label={t('glossary.toc_aria', 'Sumário do glossário')}>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-3">
            {t('glossary.toc_label', 'Atalhos')}
          </p>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map(cat => (
              <a
                key={cat}
                href={`#${catAnchor(cat)}`}
                className="px-3 py-1.5 text-sm font-semibold text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
              >
                {catLabel(cat)} ({grouped[cat].length})
              </a>
            ))}
          </div>
        </nav>

        {CATEGORIES.map(cat => (
          <section key={cat} id={catAnchor(cat)} className="scroll-mt-24">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight mb-5">
              {sectionLabel(cat)}
            </h2>
            <dl className="space-y-3">
              {grouped[cat].map(term => (
                <details key={term.id} className="group bg-white border border-gray-200 rounded-xl">
                  <summary className="cursor-pointer p-4 flex items-center justify-between list-none">
                    <dt className="font-bold text-gray-900 text-base">{termName(term)}</dt>
                    <span className="text-gray-400 group-open:rotate-180 transition-transform" aria-hidden="true">▾</span>
                  </summary>
                  <dd className="px-4 pb-4 text-sm text-gray-700 leading-relaxed">
                    {termDef(term)}
                  </dd>
                </details>
              ))}
            </dl>
          </section>
        ))}

        <section className="bg-white border border-gray-200 rounded-2xl p-6 sm:p-8 text-center">
          <p className="text-sm text-gray-600 leading-relaxed">
            <Trans i18nKey="glossary.footer" t={t}>
              Não achou um termo aqui? <Link to="/contato" className="text-primary-600 font-semibold hover:underline">Fale com a gente</Link> e a gente acrescenta. Quer aprender a usar a plataforma? <Link to="/como-jogar" className="text-primary-600 font-semibold hover:underline">Veja o guia completo</Link>.
            </Trans>
          </p>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
}
